import { Link } from "react-router-dom";
import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";
import Seo from "@/components/Seo";
import { HOTEL } from "@/data/hotel";
import { PROPERTIES, AMBICA_GALLERY } from "@/data/properties";

export default function Properties() {
  const { hash } = useLocation();

  useEffect(() => {
    if (hash) {
      const el = document.querySelector(hash);
      if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [hash]);

  return (
    <div className="pt-32 md:pt-40 pb-24">
      <Seo
        title="Our Hotels — Marwon Group properties in Delhi"
        description="Marwon Group operates two boutique hotels in Safdarjung Enclave, New Delhi — Hotel Azulo Inn (14 rooms, B1/16) and Hotel Ambica Palace (18 rooms, A1/300, near AIIMS Hospital). Founded 1980."
        path="/properties"
      />
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <Reveal>
          <div className="overline">The Group</div>
          <h1 className="mt-6 font-serif text-5xl md:text-7xl leading-[0.98] text-ink-900 max-w-3xl">
            Two hotels,
            <span className="italic text-sage-dark"> one neighbourhood.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-ink-700 leading-relaxed">
            The Marwon Group operates two boutique properties in Safdarjung Enclave, South
            Delhi — <strong className="font-medium text-ink-900">Hotel Azulo Inn</strong> at B1/16
            (near Bhikaji Cama Place) and <strong className="font-medium text-ink-900">Hotel
            Ambica Palace</strong> at A1/300 (near AIIMS Hospital). Both are quietly kept,
            walking distance from each other, and run by the same small family team.
          </p>
        </Reveal>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-20 grid grid-cols-1 md:grid-cols-2 gap-8">
        {PROPERTIES.map((p, i) => (
          <Reveal key={p.slug} delay={i * 0.06}>
            <div className="group">
              <div className="relative overflow-hidden h-[440px] md:h-[520px] bg-paper-200">
                <img
                  src={p.image}
                  alt={`${p.name} — ${p.location}`}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-900/70 via-ink-900/10 to-transparent" />
                <div className="absolute top-6 left-6 flex gap-2">
                  <span className="inline-block px-3 py-1 bg-paper-50/85 text-sage-dark text-[10px] tracking-[0.22em] uppercase font-semibold">
                    {p.kind}
                  </span>
                </div>
                <div className="absolute bottom-6 left-6 right-6">
                  <h3 className="font-serif text-3xl md:text-4xl text-paper-50">{p.name}</h3>
                  <div className="mt-2 text-[11px] tracking-[0.22em] uppercase text-paper-100/95">
                    {p.location}
                  </div>
                </div>
              </div>
              <div className="mt-6 flex items-start justify-between gap-6">
                <div>
                  <div className="overline">{p.rooms}</div>
                  <p className="mt-3 max-w-md text-ink-700 leading-relaxed">{p.blurb}</p>
                </div>
              </div>
              <div className="mt-5 flex gap-4 flex-wrap">
                {p.slug === "hotel-azulo-inn" ? (
                  <Link
                    to="/rooms"
                    data-testid={`properties-link-${p.slug}`}
                    className="text-[12px] tracking-[0.22em] uppercase text-sage-dark hover:text-sage-deep border-b border-sage/40 pb-1"
                  >
                    Visit Hotel Azulo Inn →
                  </Link>
                ) : (
                  <>
                    <a
                      href="#ambica"
                      data-testid={`properties-link-${p.slug}`}
                      className="text-[12px] tracking-[0.22em] uppercase text-sage-dark hover:text-sage-deep border-b border-sage/40 pb-1"
                    >
                      See rooms →
                    </a>
                    <a
                      href={`mailto:${HOTEL.email}?subject=Reservation%20at%20Hotel%20Ambica%20Palace`}
                      data-testid={`properties-reserve-${p.slug}`}
                      className="text-[12px] tracking-[0.22em] uppercase text-ink-700 hover:text-sage-dark border-b border-ink-900/25 pb-1"
                    >
                      Reserve →
                    </a>
                  </>
                )}
              </div>
            </div>
          </Reveal>
        ))}
      </div>

      {/* Hotel Ambica Palace — dedicated showcase section */}
      <section id="ambica" data-testid="ambica-section" className="max-w-7xl mx-auto px-6 md:px-12 mt-32 scroll-mt-32">
        <Reveal>
          <div className="grid md:grid-cols-12 gap-10 items-end mb-14">
            <div className="md:col-span-7">
              <div className="overline">Hotel Ambica Palace</div>
              <h2 className="mt-5 font-serif text-4xl md:text-5xl text-ink-900 leading-[1.05]">
                Warm interiors,
                <span className="italic text-sage-dark"> unhurried service.</span>
              </h2>
              <p className="mt-6 max-w-2xl text-ink-800 leading-[1.75]">
                Located at A1/300, Safdarjung Enclave — a short walk from AIIMS
                Hospital and Safdarjung Hospital — Hotel Ambica Palace offers eighteen
                contemporary rooms across deluxe and executive categories. Warmly lit rooms,
                pristine en-suite bathrooms, wardrobes, work desks, smart TVs, and considered
                service — an ideal address for medical travellers, business executives, and
                extended family stays.
              </p>
              <p className="mt-4 max-w-2xl text-ink-600 leading-[1.75] text-sm italic">
                A dedicated website for Hotel Ambica Palace is on its way. For now, please write to
                us at {" "}
                <a href={`mailto:${HOTEL.email}`} className="not-italic underline hover:text-sage-dark">
                  {HOTEL.email}
                </a>{" "}
                for reservations and enquiries.
              </p>
            </div>
            <div className="md:col-span-5">
              <div className="grid grid-cols-2 gap-x-6 gap-y-4 text-sm">
                {[
                  ["18", "Rooms"],
                  ["3★", "Premium"],
                  ["24/7", "Front desk"],
                  ["AIIMS", "5 minutes"],
                ].map(([n, l]) => (
                  <div key={l} className="border-l-2 border-sage/50 pl-4">
                    <div className="font-serif text-2xl text-ink-900">{n}</div>
                    <div className="overline mt-1">{l}</div>
                  </div>
                ))}
              </div>
              <a
                href={`mailto:${HOTEL.email}?subject=Reservation%20at%20Hotel%20Ambica%20Palace`}
                data-testid="ambica-reserve-btn"
                className="mt-8 inline-flex items-center gap-2 h-11 px-6 bg-sage text-paper-50 text-[11px] font-semibold tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors"
              >
                Enquire at Ambica <ArrowRight size={14} />
              </a>
            </div>
          </div>
        </Reveal>

        {/* Ambica gallery — night exterior + lobby + 5 rooms */}
        <div className="grid grid-cols-1 md:grid-cols-12 auto-rows-auto gap-4 md:gap-5">
          {[
            { src: AMBICA_GALLERY[0], span: "md:col-span-7 h-[360px] md:h-[520px]", alt: "Hotel Ambica Palace — night exterior with illuminated signboard, Safdarjung Enclave" },
            { src: AMBICA_GALLERY[1], span: "md:col-span-5 h-[360px] md:h-[520px]", alt: "Hotel Ambica Palace — deluxe king room with velvet sofa and warm sconce lighting" },
            { src: AMBICA_GALLERY[2], span: "md:col-span-5 h-[300px] md:h-[420px]", alt: "Hotel Ambica Palace — grand entrance lobby with staircase, chandelier and inlaid marble floor" },
            { src: AMBICA_GALLERY[3], span: "md:col-span-7 h-[300px] md:h-[420px]", alt: "Hotel Ambica Palace — executive king room with attached bathroom" },
            { src: AMBICA_GALLERY[4], span: "md:col-span-4 h-[280px] md:h-[400px]", alt: "Hotel Ambica Palace — cozy king room with tea tray and lounge chair" },
            { src: AMBICA_GALLERY[5], span: "md:col-span-4 h-[280px] md:h-[400px]", alt: "Hotel Ambica Palace — comfortable room with wardrobe and dressing table" },
            { src: AMBICA_GALLERY[6], span: "md:col-span-4 h-[280px] md:h-[400px]", alt: "Hotel Ambica Palace — compact executive room with smart TV" },
          ].map((img, i) => (
            <Reveal
              key={img.src}
              delay={i * 0.04}
              className={`${img.span} relative overflow-hidden bg-paper-200 group`}
            >
              <img
                src={img.src}
                alt={img.alt}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105"
              />
            </Reveal>
          ))}
        </div>

        <div className="mt-10 text-sm text-ink-600 italic">
          Address: A1/300, Safdarjung Enclave, New Delhi — 110029 · Reservations: <a href={`mailto:${HOTEL.email}`} className="not-italic underline hover:text-sage-dark">{HOTEL.email}</a>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-6 md:px-12 mt-24">
        <div className="bg-paper-100 border hairline p-10 md:p-14 grid md:grid-cols-12 gap-10 items-center">
          <div className="md:col-span-8">
            <div className="overline">For corporates & travel partners</div>
            <h3 className="mt-4 font-serif text-3xl md:text-4xl text-ink-900">
              Group rates and long-stay contracts.
            </h3>
            <p className="mt-5 max-w-2xl text-ink-700 leading-relaxed">
              We offer negotiated rates for corporate bookings, training programmes, and
              project-based long stays across both hotels. Write to our sales desk and we'll share
              a rate card and preferred-partner terms.
            </p>
          </div>
          <div className="md:col-span-4 md:text-right">
            <Link
              to="/contact?subject=Corporate%20rates"
              data-testid="properties-corporate-btn"
              className="inline-flex items-center h-12 px-8 bg-sage text-paper-50 text-[11px] font-semibold tracking-[0.22em] uppercase hover:bg-sage-dark transition-colors"
            >
              Request rate card
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
